(ns hai.tasks.deadline
  "Translates a task's deadline between its stored `:task/deadline-*`
   attributes and a structured date/time value, and derives its label,
   tooltip, whether it is overdue, and how near it is."
  (:require
   [clojure.string :as str]
   [hai.ui.components.datetime :as datetime]))

(defn- start-of-today []
  (doto (js/Date.) (.setHours 0 0 0 0)))

(defn value->params
  [value]
  (when-let [{:keys [at all-day? timezone]} (datetime/value->moment value)]
    {:task/deadline-at at
     :task/deadline-all-day? all-day?
     :task/deadline-timezone timezone}))

(defn- moment
  [{:task/keys [deadline-at deadline-all-day? deadline-timezone]}]
  {:at deadline-at :all-day? deadline-all-day? :timezone deadline-timezone})

(defn from-task [task]
  (datetime/moment->value (moment task)))

(defn label [task]
  (when (:task/deadline-at task)
    (str "Due " (datetime/relative-label (moment task)))))

(defn labels
  "The deadline's labels for less and less room, longest first: [[label]]'s
   'Due today · 4:00 PM', then 'Today · 4:00 PM', then the time alone when it
   falls today — what matters most of today's — or else the day alone. nil
   without a deadline."
  [task]
  (when-let [{:keys [day time]} (datetime/relative-parts (moment task))]
    (let [day-first (str (str/upper-case (subs day 0 1)) (subs day 1))
          with-time #(cond-> % time (str " · " time))]
      (vec (distinct [(str "Due " (with-time day))
                      (with-time day-first)
                      (if (and time (datetime/today? (moment task)))
                        time
                        day-first)])))))

(defn tooltip [task]
  (datetime/tooltip (moment task)))

(defn due-today?
  "Open task whose deadline falls on the current local day. Deliberately exact:
   a deadline from an earlier day is a different state and belongs on the tasks
   page, not on a row that says what is happening today. A timed deadline that
   has already passed *today* still counts — it is still today's work, so it is
   both `due-today?` and `overdue?`."
  [task]
  (boolean
   (and (nil? (:task/completed-at task))
        (:task/deadline-at task)
        (datetime/today? (moment task)))))

(defn overdue?
  "Open task whose deadline has passed. All-day deadlines count as overdue only
   once the day is past; timed deadlines once the moment is past."
  [task]
  (boolean
   (and (nil? (:task/completed-at task))
        (when-let [deadline (:task/deadline-at task)]
          (if (:task/deadline-all-day? task)
            (< (.getTime deadline) (.getTime (start-of-today)))
            (< (.getTime deadline) (.getTime (js/Date.))))))))

(defn closeness
  "How near an open task's deadline is: `:overdue` once it has passed, `:soon`
   when it falls within the next seven days, `:ahead` further out. nil without
   a deadline or once the task is done."
  [task]
  (when (and (nil? (:task/completed-at task))
             (:task/deadline-at task))
    (cond
      (overdue? task) :overdue
      (<= (datetime/days-from-today (moment task)) 7) :soon
      :else :ahead)))
